import React, { useEffect, useState } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import { WidgetSize } from '../../../types/Dashboard';
import { useAuth } from '../../../contexts/AuthContext';
import { firebaseService } from '../../../services/firebaseService';

interface PieChartWidgetProps {
  config: {
    chartType?: string;
    dateRange?: '7d' | '30d' | '90d' | '180d' | '365d' | 'custom';
    startDate?: string;
    endDate?: string;
  };
  size: WidgetSize;
}

const PieChartWidget: React.FC<PieChartWidgetProps> = ({ config, size }) => {
  const { userProfile } = useAuth();
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState('');

  useEffect(() => {
    loadChartData();
  }, [config.chartType, config.dateRange, config.startDate, config.endDate, userProfile?.empresaCnpj]);

  const loadChartData = async () => {
    if (!userProfile?.empresaCnpj || !config.chartType) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const charts = await firebaseService.getDashboardCharts(userProfile.empresaCnpj);
      const alunos = await firebaseService.getAlunos(userProfile.empresaCnpj);
      const avaliacoes = await firebaseService.getAvaliacoes(userProfile.empresaCnpj);

      switch (config.chartType) {
        case 'distribuicao-niveis':
          setTitle('Distribuição por Níveis');
          setData(charts.pieData || []);
          break;
        case 'distribuicao-idades':
          setTitle('Distribuição de Idades');
          const faixasEtarias: { [key: string]: number } = {};
          alunos.forEach(aluno => {
            if (aluno.dataNascimento) {
              const nascimento = new Date(aluno.dataNascimento);
              const idade = new Date().getFullYear() - nascimento.getFullYear();
              let faixa = '';
              if (idade < 6) faixa = '0-5 anos';
              else if (idade < 11) faixa = '6-10 anos';
              else if (idade < 16) faixa = '11-15 anos';
              else if (idade < 21) faixa = '16-20 anos';
              else faixa = '21+ anos';
              faixasEtarias[faixa] = (faixasEtarias[faixa] || 0) + 1;
            }
          });
          setData(Object.entries(faixasEtarias).map(([name, value]) => ({
            name,
            value,
            color: `#${Math.floor(Math.random()*16777215).toString(16)}`
          })));
          break;
        case 'distribuicao-notas':
          setTitle('Distribuição de Notas');
          const notasDistribuicao: { [key: string]: number } = {
            '0.0-0.2': 0,
            '0.2-0.4': 0,
            '0.4-0.6': 0,
            '0.6-0.8': 0,
            '0.8-1.0': 0
          };
          avaliacoes.forEach(av => {
            const nota = av.notaGeral || 0;
            if (nota < 0.2) notasDistribuicao['0.0-0.2']++;
            else if (nota < 0.4) notasDistribuicao['0.2-0.4']++;
            else if (nota < 0.6) notasDistribuicao['0.4-0.6']++;
            else if (nota < 0.8) notasDistribuicao['0.6-0.8']++;
            else notasDistribuicao['0.8-1.0']++;
          });
          setData(Object.entries(notasDistribuicao).map(([name, value]) => ({
            name,
            value,
            color: value > 0 ? '#3b82f6' : '#e5e7eb'
          })));
          break;
        default:
          setTitle('Distribuição');
          setData(charts.pieData || []);
      }
    } catch (error) {
      console.error('Erro ao carregar dados do gráfico:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-300"></div>
      </div>
    );
  }

  const COLORS = ['#3b82f6', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

  return (
    <div className="h-full flex flex-col min-h-0">
      {title && (
        <h3 className="text-sm font-semibold text-gray-900 mb-4 flex-shrink-0">{title}</h3>
      )}
      <div className="flex-1 min-h-0">
        <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={data}
            cx="50%"
            cy="50%"
            labelLine={false}
            label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
            outerRadius={size === 'small' ? 60 : size === 'medium' ? 80 : 100}
            fill="#8884d8"
            dataKey="value"
          >
            {data.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color || COLORS[index % COLORS.length]} />
            ))}
          </Pie>
          <Tooltip
            contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px'
            }}
          />
        </PieChart>
      </ResponsiveContainer>
      </div>
    </div>
  );
};

export default PieChartWidget;

